<template>
  <v-container fluid class="direct-payment-page">
    <!-- Header con gradient -->
    <v-card class="page-header mb-4" elevation="3">
      <div class="header-content">
        <div class="d-flex align-center">
          <v-avatar size="56" color="white" class="mr-4 elevation-2">
            <v-icon size="32" color="primary">mdi-receipt-text-arrow-right</v-icon>
          </v-avatar>
          <div>
            <h1 class="page-title">Cobros Directos</h1>
            <p class="page-subtitle">Registro y gestión de cobros sin factura</p>
          </div>
        </div>
        <v-spacer></v-spacer>
        <div class="header-actions">
          <v-btn
            v-if="hasFeature('CXC-042')"
            color="white"
            class="text-primary action-btn"
            @click="openPaymentDialog(2)"
            elevation="2"
          >
            <v-icon left color="success">mdi-cash-plus</v-icon>
            Nuevo Cobro
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- Filtros (search header) -->
    <v-card class="filters-card mb-4" outlined>
      <v-card-text class="pa-3">
        <search-header
          @dataFilters="changeBusiness($event)"
          :selected_week="2"
          :type="false"
        />
      </v-card-text>
    </v-card>

    <!-- KPIs -->
    <v-row class="mb-4" no-gutters>
      <v-col cols="12" md="4" class="px-1">
        <v-card class="kpi-card" outlined>
          <div class="kpi-icon-wrapper kpi-blue">
            <v-icon color="white" size="28">mdi-format-list-numbered</v-icon>
          </div>
          <div class="kpi-content">
            <div class="kpi-label">Total registros</div>
            <div class="kpi-value">{{ direct_payments.length }}</div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" md="4" class="px-1">
        <v-card class="kpi-card" outlined>
          <div class="kpi-icon-wrapper kpi-green">
            <v-icon color="white" size="28">mdi-cash-plus</v-icon>
          </div>
          <div class="kpi-content">
            <div class="kpi-label">Total cobrado</div>
            <div class="kpi-value">{{ currencyFormat(totalCobrosAmount) }}</div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" md="4" class="px-1">
        <v-card class="kpi-card" outlined>
          <div class="kpi-icon-wrapper kpi-orange">
            <v-icon color="white" size="28">mdi-progress-clock</v-icon>
          </div>
          <div class="kpi-content">
            <div class="kpi-label">Pendiente por consumir</div>
            <div class="kpi-value">{{ currencyFormat(totalPendiente) }}</div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabla principal -->
    <v-card class="data-card" elevation="2">
      <v-toolbar flat class="data-toolbar">
        <v-icon left color="primary">mdi-table</v-icon>
        <v-toolbar-title class="text-subtitle-1 font-weight-medium">
          Listado
        </v-toolbar-title>
        <v-chip x-small color="primary" outlined class="ml-2 font-weight-medium">
          {{ direct_payments.length }}
        </v-chip>
        <v-spacer></v-spacer>

        <v-text-field
          v-model="search"
          label="Buscar..."
          outlined
          dense
          hide-details
          prepend-inner-icon="mdi-magnify"
          clearable
          style="max-width: 280px"
        />
      </v-toolbar>

      <v-divider></v-divider>

      <v-expand-transition>
        <div v-if="loading" class="pa-6">
          <v-skeleton-loader type="table-thead, table-tbody" />
        </div>

        <v-data-table
          v-else
          :search="search"
          :headers="headers"
          :items="direct_payments"
          class="elevation-0 modern-table"
          :items-per-page="15"
          :footer-props="{
            itemsPerPageOptions: [10, 15, 25, 50, -1],
            itemsPerPageText: 'Filas por página',
          }"
        >
          <!-- Columna ID con link -->
          <template v-slot:[`item.prepayment_id`]="{ item }">
            <v-hover v-slot="{ hover }">
              <div
                @click="showDirectPayment(item.prepayment_id)"
                class="payment-id-cell"
                :class="{ 'payment-id-hover': hover }"
              >
                <v-icon left small color="primary">mdi-pound</v-icon>
                <span>{{ filLeft(item.prepayment_id.toString(), 8, "0") }}</span>
              </div>
            </v-hover>
          </template>

          <!-- Fecha -->
          <template v-slot:[`item.payment_date`]="{ item }">
            <div class="date-cell">
              <v-icon x-small color="grey">mdi-calendar</v-icon>
              <span class="ml-1">{{ formatDate(item.payment_date) }}</span>
            </div>
          </template>

          <!-- Entidad con avatar -->
          <template v-slot:[`item.entity_name`]="{ item }">
            <div class="entity-cell">
              <v-avatar size="28" color="green lighten-2" class="mr-2 white--text">
                <span class="font-weight-bold caption">
                  {{ entityInitials(item.entity_name) }}
                </span>
              </v-avatar>
              <span class="entity-name">
                {{ item.entity_name || '— Sin entidad —' }}
              </span>
            </div>
          </template>

          <!-- Total -->
          <template v-slot:[`item.total_amount`]="{ item }">
            <div class="amount-cell">
              <span class="amount-value font-weight-bold green--text text--darken-2">
                {{ currencyFormat(item.total_amount) }}
              </span>
              <span class="amount-currency caption grey--text">
                {{ item.currency_code }}
              </span>
            </div>
          </template>

          <!-- Consumido con barra de progreso -->
          <template v-slot:[`item.consumed`]="{ item }">
            <div class="consumed-cell">
              <div class="d-flex justify-space-between align-center mb-1">
                <span class="caption">
                  {{ currencyFormat(item.consumed) }}
                </span>
                <span class="caption font-weight-medium" :class="consumedPercentClass(item)">
                  {{ consumedPercent(item) }}%
                </span>
              </div>
              <v-progress-linear
                :value="consumedPercent(item)"
                :color="consumedColor(item)"
                height="4"
                rounded
              />
            </div>
          </template>

          <!-- Acciones -->
          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex justify-end">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="primary"
                    @click="showDirectPayment(item.prepayment_id)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon small>mdi-eye</v-icon>
                  </v-btn>
                </template>
                <span>Ver detalle</span>
              </v-tooltip>

              <v-tooltip top v-if="item.consumed == 0 && hasFeature('CXC-042')">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="error"
                    @click="deleteItem(item)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon small>mdi-close-circle</v-icon>
                  </v-btn>
                </template>
                <span>Anular</span>
              </v-tooltip>
            </div>
          </template>

          <!-- Empty state -->
          <template v-slot:no-data>
            <div class="empty-state py-10">
              <v-icon size="80" color="grey lighten-1">mdi-database-off-outline</v-icon>
              <h3 class="mt-4 grey--text text--darken-1">No hay cobros</h3>
              <p class="caption grey--text">
                Crea un nuevo cobro directo para comenzar
              </p>
              <v-btn
                v-if="hasFeature('CXC-042')"
                color="primary"
                outlined
                small
                class="mt-2"
                @click="openPaymentDialog(2)"
              >
                <v-icon left small>mdi-plus</v-icon>
                Crear primer cobro
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-expand-transition>
    </v-card>

    <!-- Dialog de pago -->
    <v-dialog transition="slide-x-transition" v-model="dialog" fullscreen>
      <PaymentDialog
        :dialog="dialog"
        :type="paymentType"
        :business-units="$store.state.auth.bussines_units || []"
        :locations="$store.state.auth.locations || []"
        :departments="itemsDepartment || []"
        :payments-type="paymentsType || []"
        :currencies="currencies || []"
        :entity-payment-types="entity_Payment_types || []"
        :category-dgii="categoryDgii || []"
        :accounts="accounts || []"
        :categories="categories || []"
        :filters="filters"
        @close="closePaymentDialog"
        @saved="onPaymentSaved"
        @update-currencies="updateCurrencies"
      />
    </v-dialog>

    <!-- Dialog confirmar anulación -->
    <v-dialog v-model="dialogDelete" max-width="460px">
      <v-card>
        <v-card-title class="text-h6 error--text">
          <v-icon left color="error">mdi-alert-circle</v-icon>
          Confirmar anulación
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          ¿Estás seguro de que deseas anular este registro?
          <strong>Esta acción no se puede deshacer.</strong>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn text @click="closeDelete">Cancelar</v-btn>
          <v-btn color="error" depressed @click="deleteItemConfirm">
            <v-icon left small>mdi-delete</v-icon>
            Anular
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from "moment";
import PaymentDialog from "@/components/PaymentDialog.vue";

export default {
  components: {
    PaymentDialog,
  },
  data: () => ({
    search: "",
    loading: false,
    dialog: false,
    dialogDelete: false,
    filters: null,
    paymentType: 2, // fijo en cobro
    direct_payments: [],
    itemsDepartment: [],
    paymentsType: [],
    currencies: [],
    entity_Payment_types: [],
    categoryDgii: [],
    accounts: [],
    categories: [],
    itemToDelete: null,
    headers: [
      { text: "ID", value: "prepayment_id", align: "start", width: 110 },
      { text: "Fecha", value: "payment_date", align: "start", width: 120 },
      { text: "Unidad negocio", value: "business_unit_description" },
      { text: "Ubicación", value: "location_description" },
      { text: "Cliente", value: "entity_name" },
      { text: "Total", value: "total_amount", align: "end", width: 160 },
      { text: "Consumido", value: "consumed", align: "start", width: 180 },
      { text: "Acciones", value: "actions", align: "end", sortable: false, width: 110 },
    ],
  }),
  computed: {
    totalCobrosAmount() {
      return this.direct_payments.reduce(
        (sum, p) => sum + parseFloat(p.total_amount || 0),
        0
      );
    },
    totalPendiente() {
      return this.direct_payments.reduce((sum, p) => {
        const total = parseFloat(p.total_amount || 0);
        const consumed = parseFloat(p.consumed || 0);
        return sum + Math.max(0, total - consumed);
      }, 0);
    },
  },
  created() {
    this.init();
  },
  methods: {
    formatDate(date) {
      return date ? moment(date).format("DD/MM/YYYY") : "—";
    },
    entityInitials(name) {
      if (!name) return "?";
      const parts = name.trim().split(/\s+/);
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return (parts[0][0] + parts[1][0]).toUpperCase();
    },
    consumedPercent(item) {
      const total = parseFloat(item.total_amount || 0);
      const consumed = parseFloat(item.consumed || 0);
      if (total <= 0) return 0;
      return Math.min(100, Math.round((consumed / total) * 100));
    },
    consumedColor(item) {
      const pct = this.consumedPercent(item);
      if (pct === 0) return "grey lighten-1";
      if (pct < 50) return "orange";
      if (pct < 100) return "blue";
      return "success";
    },
    consumedPercentClass(item) {
      const pct = this.consumedPercent(item);
      if (pct === 0) return "grey--text";
      if (pct < 100) return "blue--text text--darken-1";
      return "success--text text--darken-1";
    },

    async init() {
      try {
        this.itemsDepartment = await this.g_departments();

        const resultTypePayments = await this.api_Get("income/payments_types");
        this.paymentsType = await resultTypePayments.json();

        const entity_Payment_types = await this.api_Get("income/entity_Payment_types");
        const entityPaymentTypesData = await entity_Payment_types.json();
        this.entity_Payment_types = Array.isArray(entityPaymentTypesData)
          ? entityPaymentTypesData
          : [];

        let resultTypeCategories = await this.api_Get("accounting/categories_diary_book");
        this.categories = await resultTypeCategories.json();

        let resultTypeAccounts = await this.api_Get("accounting/accountsUsable");
        const accountsData = await resultTypeAccounts.json();
        // Las cuentas de sistema solo se usan en entradas de diario: se muestran pero no se pueden elegir
        this.accounts = accountsData.map((element) => ({
          name: `${element.code} - ${element.name}${element.is_system_only ? " (sistema)" : ""}`,
          id: element.id,
          dgii_category_id: element.dgii_category_id,
          disabled: !!element.is_system_only,
        }));

        let resultCategoryDgii = await this.api_Get("accounting/category_dgii");
        const dataCategoryDgii = await resultCategoryDgii.json();
        this.categoryDgii = dataCategoryDgii.map((item) => ({
          text: item.name,
          value: item.id,
        }));

        this.itemsDepartment = this.itemsDepartment.map((dept) => ({
          text: dept.text || dept.name,
          value: dept.value || dept.id,
        }));
      } catch (error) {
        console.error("Error inicializando datos:", error);
        this.$toast.error("Error cargando datos iniciales");
      }
    },

    openPaymentDialog(type) {
      this.paymentType = type;
      this.dialog = true;
    },

    closePaymentDialog() {
      this.dialog = false;
    },

    onPaymentSaved(data) {
      this.$toast.success("Cobro guardado exitosamente");
      this.showDirectPayment(data[0].id);
      this.getData(this.filters);
    },

    updateCurrencies(currencies) {
      this.currencies = currencies;
    },

    showDirectPayment(id) {
      const route = this.$router.resolve({
        name: "ViewDirectPayment",
        params: { id: id },
      });
      window.open(route.href);
    },

    async getData(data) {
      this.loading = true;
      let filters = "where invoice_type_id IN (2,3) ";
      filters +=
        data.business == null ? "" : " AND BUSINESS_UNIT_ID IN (" + data.business + ") ";
      filters +=
        data.location == null || data.location == 0
          ? ""
          : " AND LOCATION_ID IN (" + data.location + ")";
      filters +=
        data.department_id == null || data.department_id == 0
          ? ""
          : " AND DEPARTMENT_ID = " + data.department_id + " ";
      filters +=
        " AND PAYMENT_DATE::DATE BETWEEN '" +
        data.dateStart +
        "' AND '" +
        data.dateEnd +
        "' ";

      try {
        const result = await this.api_Post("transactions/all_direct_payments", {
          filters: filters,
        });
        this.direct_payments = await result.json();
      } catch (error) {
        this.$toast.error("Error al cargar registros");
      } finally {
        this.loading = false;
      }
    },

    changeBusiness(data) {
      if (data.business == 0) {
        this.getData({
          business: this.$store.state.auth.user.acess_business_unit_finance,
          location: null,
          dateStart: data.dateStart,
          dateEnd: data.dateEnd,
        });
      } else {
        this.getData(data);
      }
      this.filters = data;
    },

    deleteItem(item) {
      this.itemToDelete = item;
      this.dialogDelete = true;
    },

    async deleteItemConfirm() {
      if (this.itemToDelete) {
        const responseRequest = await this.api_Post(
          "transactions/remove_payment_direct",
          { id: this.itemToDelete.prepayment_id }
        );

        if (responseRequest.status == 200) {
          this.$toast.success("Registro anulado");
          this.getData(this.filters);
        } else {
          this.$toast.error("Error al anular");
        }
      }
      this.closeDelete();
    },

    closeDelete() {
      this.dialogDelete = false;
      this.itemToDelete = null;
    },

    filLeft(str, length, char) {
      return str.padStart(length, char);
    },
    currencyFormat(amount) {
      return new Intl.NumberFormat("es-DO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(parseFloat(amount || 0));
    },
  },
};
</script>

<style scoped>
.direct-payment-page {
  background-color: #f4f6f9;
  min-height: 100vh;
  padding: 16px;
}

/* Header */
.page-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  border-radius: 12px !important;
  overflow: hidden;
}

.header-content {
  display: flex;
  align-items: center;
  padding: 20px 28px;
  color: white;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  color: white;
  letter-spacing: 0.5px;
}

.page-subtitle {
  font-size: 13px;
  margin: 2px 0 0;
  color: rgba(255, 255, 255, 0.85);
}

.action-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px;
}

.text-primary {
  color: #1976d2 !important;
}

/* Filters card */
.filters-card {
  border-radius: 10px !important;
  border-color: #e0e0e0 !important;
}

/* KPI cards */
.kpi-card {
  border-radius: 10px !important;
  border-color: #e0e0e0 !important;
  display: flex;
  align-items: center;
  padding: 16px;
  height: 100%;
  transition: all 0.25s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08) !important;
}

.kpi-icon-wrapper {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  flex-shrink: 0;
}

.kpi-blue { background: linear-gradient(135deg, #2196f3, #1976d2); }
.kpi-red { background: linear-gradient(135deg, #ef5350, #d32f2f); }
.kpi-green { background: linear-gradient(135deg, #66bb6a, #2e7d32); }
.kpi-orange { background: linear-gradient(135deg, #ffa726, #f57c00); }

.kpi-content {
  flex: 1;
  min-width: 0;
}

.kpi-label {
  font-size: 12px;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.kpi-value {
  font-size: 22px;
  font-weight: 700;
  color: #212121;
  line-height: 1.2;
  margin-top: 2px;
}

.kpi-subtitle {
  font-size: 12px;
  color: #9e9e9e;
  margin-top: 2px;
}

/* Data card */
.data-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.data-toolbar {
  background-color: #fafbfc !important;
  border-bottom: 1px solid #e9ecef;
  padding: 0 16px;
}

.modern-table ::v-deep table thead tr th {
  background-color: #f8f9fa !important;
  color: #495057 !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e9ecef !important;
}

.modern-table ::v-deep table tbody tr {
  transition: background-color 0.2s ease;
}

.modern-table ::v-deep table tbody tr:hover {
  background-color: rgba(25, 118, 210, 0.04) !important;
}

/* Cells */
.payment-id-cell {
  display: inline-flex;
  align-items: center;
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: #424242;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.payment-id-hover {
  background-color: #e3f2fd;
  color: #1565c0 !important;
}

.date-cell {
  font-size: 13px;
  color: #616161;
}

.entity-cell {
  display: flex;
  align-items: center;
}

.entity-name {
  font-weight: 500;
  color: #212121;
  font-size: 14px;
}

.amount-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.2;
}

.amount-value {
  font-size: 15px;
  font-family: 'Roboto Mono', monospace;
}

.amount-currency {
  font-size: 11px;
  margin-top: 2px;
}

.consumed-cell {
  min-width: 140px;
}

/* Empty state */
.empty-state {
  text-align: center;
}

/* Responsive */
@media (max-width: 960px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }
}
</style>
